/* eslint-disable react/prop-types */
// This repo carries no prop-types dependency and no component declares them;
// disabling the rule here keeps the lint gate at zero new problems without
// adding a dependency for one file.
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/components/utils/translations';
import { Loader2, AlertCircle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { fetchReplacementCycleRadar, fetchReplacementCyclePage } from '@/lib/api/procurementServer';
import { resolveDisplacementScope } from '@/lib/displacementTenantScopes';
import { SENTIMENT_ROWS } from '@/lib/categoryIntel';
import {
  hardwareKey, HARDWARE_LABEL, bandsFor, bandRangeLabel, REPLACEMENT_BANDS, REPLACEMENT_BAND_LABEL_KEYS,
  REPLACEMENT_WINDOW_BANDS,
} from '@/lib/replacementCycle';

// Same palette and building blocks as CategoryDisplacement, so a hardware tenant's radar reads
// like every other tenant's. Only the measures differ: age instead of time-to-expiry, and maker
// changes at re-purchase instead of annual churn.
const TONE = {
  hot: { text: 'text-[#D94C3D]', bg: '#D94C3D' },
  warn: { text: 'text-[#C8860D]', bg: '#C8860D' },
  good: { text: 'text-[#1E9E6A]', bg: '#1E9E6A' },
  none: { text: 'text-muted-foreground', bg: '#8A9BB0' },
};
const SENTIMENT_LABEL_KEYS = { neg: 'sentimentNegative', neu: 'sentimentNeutral', pos: 'sentimentPositive', unk: 'unknown' };
// Hardware sentiment is mostly unrecorded, so the grid carries an Unknown row the software radar does not need.
const GRID_ROWS = [...SENTIMENT_ROWS, { key: 'unk', label: 'Unknown', tone: 'none' }];
// Oldest first, as in Contracts Intelligence; the pipeline bars run red → grey like the renewal pipeline.
const BAND_COLORS = { overdue: '#D94C3D', due: '#E07A2E', plan: '#C8860D', mid: '#00C2C2', new: '#CFD7DE', undated: '#E5EAF0' };
const DEFAULT_SCOPES = ['United Kingdom', 'United States'];

const SENT_PARAM = { neg: 'negative', neu: 'neutral', pos: 'positive', unk: 'unknown' };

function shortName(category) {
  return String(category || '')
    .replace(/Library Self-Service, RFID & Automated Handling/i, 'Library Self-Service & RFID')
    .replace(/Robotics, Automation & Industry 4\.0 Training/i, 'Robotics & Automation');
}

function Kpi({ label, value, unit, sub, tone, help }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span>{label}</span>
        {help ? (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger
                type="button"
                aria-label={t('procHowLabelIsMeasured', { label })}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2]"
              >
                <Info className="h-3 w-3" aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[320px] space-y-2 text-left normal-case tracking-normal">
                {help}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
      <div className={`mt-1.5 text-3xl font-semibold tabular-nums leading-none ${tone || 'text-[#0A1A2F] dark:text-foreground'}`}>
        {value}{unit ? <span className="text-base font-semibold"> {unit}</span> : null}
      </div>
      <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function Panel({ title, hint, children }) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-baseline justify-between gap-3 border-b border-border px-5 py-3.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint ? <span className="text-right text-xs text-muted-foreground">{hint}</span> : null}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

/** Share of installs 5+ years old, by one dimension; rows are [label, pct, n], largest base first. */
function AgeingChart({ title, hint, rows, emptyNote }) {
  const { t } = useTranslation();
  const max = Math.max(...rows.map((r) => r[1]), 1);
  return (
    <Panel title={title} hint={hint}>
      {rows.length === 0 ? (
        <p className="py-4 text-xs text-muted-foreground">{emptyNote}</p>
      ) : (
        <div className="space-y-3">
          {rows.map(([label, pct, n]) => (
            <div key={label} className="grid grid-cols-[132px_1fr_56px] items-center gap-3 text-[13px]">
              <span className="truncate font-medium text-muted-foreground" title={label}>{label}</span>
              <div className="h-[22px] overflow-hidden rounded border border-border bg-muted" title={t('procRcOfInstalls', { n: Number(n).toLocaleString() })}>
                <div className="h-full rounded-l bg-[#C8860D]" style={{ width: `${Math.max(2, (pct / max) * 100)}%` }} />
              </div>
              <span className="text-right font-semibold tabular-nums">{Number(pct).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/**
 * [2026-11-03] Displacement Radar for HARDWARE tenants (bibliotheca, niryo), laid out like the
 * software radar (CategoryDisplacement): header, category switcher, market scope, KPI row,
 * pipeline + net position, three breakdowns, and the incumbent grid with account drill-down.
 * Kit is bought every 7+ years, so the pipeline is equipment AGE (from the last system
 * purchase) and the churn measure is what happened at each re-purchase 5+ years on.
 */
export default function HardwareReplacementRadar({ tenantId, categories = [] }) {
  const { t } = useTranslation();
  // One page per hardware KEY: "IT Hardware" and "IT Hardware (Education only)" are one category.
  const hwKeys = useMemo(() => [...new Set((categories || []).map(hardwareKey).filter(Boolean))], [categories]);
  const scope = useMemo(() => resolveDisplacementScope(tenantId), [tenantId]);
  const COUNTRY_SCOPES = scope ? scope.countries : DEFAULT_SCOPES;
  const [active, setActive] = useState(null);
  const [country, setCountry] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cell, setCell] = useState(null);
  const [cellRows, setCellRows] = useState([]);
  const [cellTotal, setCellTotal] = useState(0);
  const [cellLoading, setCellLoading] = useState(false);

  const hwKeysKey = hwKeys.join('|');
  useEffect(() => { setActive((p) => (hwKeys.includes(p) ? p : hwKeys[0] ?? null)); }, [hwKeysKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setCell(null); setCellRows([]); }, [active, country]);

  useEffect(() => {
    if (!tenantId || !active) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchReplacementCycleRadar(tenantId, HARDWARE_LABEL[active], country)
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [tenantId, active, country]);

  // The accounts behind one grid cell, loaded on click like the software radar's.
  useEffect(() => {
    if (!cell || !tenantId || !active) return undefined;
    let cancelled = false;
    setCellLoading(true);
    fetchReplacementCyclePage(tenantId, {
      category: HARDWARE_LABEL[active], countries: country ? [country] : [],
      sentiments: [SENT_PARAM[cell.key]], bands: [cell.band], sort: 'age', dir: 'desc', pageSize: 200,
    })
      .then((d) => { if (!cancelled) { setCellRows(d?.rows || []); setCellTotal(Number(d?.total) || 0); setCellLoading(false); } })
      .catch(() => { if (!cancelled) { setCellRows([]); setCellTotal(0); setCellLoading(false); } });
    return () => { cancelled = true; };
  }, [cell, tenantId, active, country]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('procLoadingCategoryIntel')}
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-destructive">
        <AlertCircle className="w-4 h-4 mr-2" /> {error}
      </div>
    );
  }
  const r = data;
  if (!r || !Number(r.installs)) {
    return <div className="py-24 text-center text-sm text-muted-foreground">{t('procNoMeasuredHistory')}</div>;
  }
  const b = r.bands_years || bandsFor(active);
  const matrix = r.matrix || {};
  const cellN = (band, k) => Number(matrix?.[band]?.[k] || 0);
  const bandCount = Object.fromEntries(REPLACEMENT_BANDS.map((band) => [band, ['neg', 'neu', 'pos', 'unk'].reduce((a, k) => a + cellN(band, k), 0)]));
  const inWindow = REPLACEMENT_WINDOW_BANDS.reduce((a, band) => a + bandCount[band], 0);
  const dueOrOver = bandCount.due + bandCount.overdue;
  const events = Number(r.replacements?.events || 0);
  const switched = Number(r.replacements?.switched || 0);
  const minEvents = Number(r.min_events_for_rate || 20);
  const rep = { events, switched, retained: Number(r.replacements?.retained || 0), min_events_for_rate: minEvents,
    switch_rate: events >= minEvents ? Math.round((switched / events) * 1000) / 10 : null };
  const pipeMax = Math.max(...REPLACEMENT_BANDS.map((band) => bandCount[band] || 0), 1);
  const winners = r.net_winners || [];
  const losers = r.net_losers || [];
  const netMax = Math.max(1, ...[...winners, ...losers].map((m) => Math.abs(m.n)));

  return (
    <div className="space-y-4">
      {/* page header — the category IS the page */}
      <div className="flex flex-wrap items-start justify-between gap-5 border-b border-border pb-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-[#00A3A3]">{t('displacementRadar')}</div>
          <h2 className="mt-1.5 text-2xl font-semibold tracking-tight">{HARDWARE_LABEL[active] || active}</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('procRcRadarSubtitle')}</p>
        </div>
        <div className="whitespace-nowrap text-right text-xs text-muted-foreground">
          <span className="mr-1.5 inline-block h-[7px] w-[7px] rounded-full bg-[#00C2C2] align-middle" />
          {t('procLastUpdatedDate', { date: String(data?.computed_at || '').slice(0, 10) })}
        </div>
      </div>

      {hwKeys.length > 1 && (
        <nav className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
          <span className="mr-1 border-r border-border pr-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('procYourCategories')}
          </span>
          {hwKeys.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              aria-current={c === active ? 'page' : undefined}
              className={`rounded-md px-3 py-1.5 text-[13px] transition-colors ${
                c === active
                  ? 'bg-[#0A1A2F] font-semibold text-white dark:bg-[#00C2C2] dark:text-[#0A1A2F]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {shortName(HARDWARE_LABEL[c] || c)}
            </button>
          ))}
        </nav>
      )}

      {COUNTRY_SCOPES.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('market')}</span>
          {[null, ...COUNTRY_SCOPES].map((c) => (
            <button
              key={c || 'global'}
              type="button"
              onClick={() => setCountry(c)}
              className={`rounded-md px-3 py-1.5 text-[13px] transition-colors ${
                country === c ? 'bg-[#0A1A2F] font-semibold text-white dark:bg-[#00C2C2] dark:text-[#0A1A2F]'
                              : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              {c || t('procGlobal')}
            </button>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi
          label={t('procRcSwitchRate')}
          value={rep.switch_rate == null ? '—' : `${rep.switch_rate}%`}
          sub={rep.switch_rate == null
            ? t('procRcSwitchTooFew', { n: rep.events || 0, min: rep.min_events_for_rate || 20 })
            : t('procRcSwitchSub', { switched: rep.switched, retained: rep.retained })}
          help={<p>{t('procRcSwitchHelp', { n: b.plan })}</p>}
        />
        <Kpi label={t('procRcMakersPerAccount')} value={r.makers_per_account ?? '—'} sub={t('procPctBuyersUseOne', { n: Number(r.pct_single || 0) })} />
        <Kpi label={t('procRcKpiWindow')} value={inWindow.toLocaleString()} sub={t('procRcKpiWindowSub', { n: b.plan })} tone={TONE.warn.text}
             help={<p>{t('procRcAgeHelp', { a: b.plan, b: b.due })}</p>} />
        <Kpi label={t('procRcKpiDue')} value={dueOrOver.toLocaleString()} sub={t('procRcKpiDueSub', { n: b.due })} tone={TONE.hot.text} />
        <Kpi label={t('procRcUpOwn')} value={(r.up_for_replacement?.own ?? 0).toLocaleString()} sub={t('procRcUpOwnSub', { n: b.plan })} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t('procRcPipelineTitle')} hint={t('procRcPipelineHint')}>
          <div className="space-y-3">
            {REPLACEMENT_BANDS.map((band) => (
              <div key={band} className="grid grid-cols-[112px_1fr_56px] items-center gap-3 text-[13px]">
                <span className="font-medium text-muted-foreground">
                  {t(REPLACEMENT_BAND_LABEL_KEYS[band])}
                  <span className="block text-[11px] font-normal">{bandRangeLabel(band, b, t)}</span>
                </span>
                <div className="h-[22px] overflow-hidden rounded border border-border bg-muted">
                  <div className="h-full rounded-l" style={{ width: `${Math.max(2, ((bandCount[band] || 0) / pipeMax) * 100)}%`, background: BAND_COLORS[band] }} />
                </div>
                <span className="text-right font-semibold tabular-nums">{(bandCount[band] || 0).toLocaleString()}</span>
              </div>
            ))}
            <p className="border-t border-border pt-3 text-xs text-muted-foreground">
              {t('procRcPipelineNoteBefore')}{' '}
              <strong className="text-foreground">{Number(r.up_for_replacement?.competitor || 0).toLocaleString()}</strong>{' '}
              {t('procRcPipelineNoteAfter', { n: b.plan })}
            </p>
          </div>
        </Panel>

        <Panel title={t('procNetPositionChange')} hint={t('procRcNetHint')}>
          <div className="space-y-5">
            {!winners.length && !losers.length && (
              <p className="text-xs text-muted-foreground">{t('procRcNoMoves')}</p>
            )}
            {[[t('procNetWinners'), winners, true], [t('procNetLosers'), losers, false]].map(([title, rows, positive]) => (rows.length ? (
              <div key={title} className="space-y-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
                {rows.map((m) => (
                  <div key={m.supplier} className="grid grid-cols-[1fr_88px_44px] items-center gap-3 text-[13px]">
                    <div className="truncate font-medium" title={m.supplier}>{m.supplier}</div>
                    <div className="h-[16px] rounded-sm bg-muted" aria-hidden="true">
                      <div className="h-full rounded-sm" style={{ width: `${Math.max(3, (Math.abs(m.n) / netMax) * 100)}%`, background: positive ? '#00C2C2' : '#D94C3D' }} />
                    </div>
                    <span className={`text-right font-semibold tabular-nums ${positive ? 'text-[#00A3A3]' : TONE.hot.text}`}>{positive ? `+${m.n}` : m.n}</span>
                  </div>
                ))}
              </div>
            ) : null))}
            <p className="border-t border-border pt-3 text-xs text-muted-foreground">{t('procRcMovesHelp', { events: rep.events || 0, n: b.plan })}</p>
          </div>
        </Panel>
      </div>

      <div className={`grid gap-4 ${country ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}>
        <AgeingChart title={t('procRcAgeingByMaker')} hint={t('procRcAgeingHint', { n: b.plan })} rows={r.by_maker || []} emptyNote={t('procRcAgeingEmpty')} />
        {!country && (
          <AgeingChart title={t('procRcAgeingByCountry')} hint={t('procRcAgeingHint', { n: b.plan })} rows={r.by_country || []} emptyNote={t('procRcAgeingEmpty')} />
        )}
        <AgeingChart title={t('procRcAgeingByType')} hint={t('procRcAgeingHint', { n: b.plan })} rows={r.by_type || []} emptyNote={t('procRcAgeingEmpty')} />
      </div>

      {/* sentiment x replacement window */}
      <Panel title={t('procRcGridTitle')}>
        <div className="overflow-x-auto">
          <div className="grid min-w-[640px] grid-cols-[120px_repeat(6,1fr)] gap-1.5">
            <div className="pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('procIncumbent')}</div>
            {REPLACEMENT_BANDS.map((band) => (
              <div key={band} className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t(REPLACEMENT_BAND_LABEL_KEYS[band])}
                <span className="block normal-case tracking-normal font-normal">{bandRangeLabel(band, b, t)}</span>
              </div>
            ))}
            {GRID_ROWS.map(({ key, tone }) => {
              const vals = REPLACEMENT_BANDS.map((band) => cellN(band, key));
              const rowMax = Math.max(...vals, 1);
              return (
                <Fragment key={key}>
                  <div className="flex items-center gap-2 pr-2 text-[13px] font-semibold">
                    <i className="h-2.5 w-2.5 flex-none rounded-sm" style={{ background: TONE[tone].bg }} />
                    {t(SENTIMENT_LABEL_KEYS[key])}
                  </div>
                  {REPLACEMENT_BANDS.map((b, bi) => {
                    const n = vals[bi];
                    // Unhappy incumbent on kit already in its window: the hottest cell, outlined like the software radar's.
                    const priority = key === 'neg' && REPLACEMENT_WINDOW_BANDS.includes(b) && n > 0;
                    return (
                      <button
                        key={b}
                        type="button"
                        disabled={n === 0}
                        onClick={() => setCell({ key, band: b })}
                        className={`relative rounded-md border px-2 py-2.5 text-center transition-transform ${
                          n === 0 ? 'cursor-default border-border opacity-45' : 'border-border hover:-translate-y-px hover:border-[#00C2C2]'
                        } ${cell && cell.key === key && cell.band === b ? 'ring-2 ring-[#0A1A2F] dark:ring-[#00C2C2]' : ''}`}
                        style={n === 0 ? undefined : {
                          background: `color-mix(in srgb, ${TONE[tone].bg} ${Math.round((0.14 + 0.5 * (n / rowMax)) * 100)}%, transparent)`,
                          boxShadow: priority ? 'inset 0 0 0 2px #D94C3D' : undefined,
                        }}
                      >
                        <span className="block text-lg font-semibold leading-tight tabular-nums">{n.toLocaleString()}</span>
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">{n === 1 ? t('procAccountLower') : t('procAccountsLower')}</span>
                      </button>
                    );
                  })}
                </Fragment>
              );
            })}
          </div>
        </div>

        {cell && (
          <div className="mt-5 border-t border-border pt-4">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h4 className="text-[13px] font-semibold">
                {t('procSentimentIncumbent', { sentiment: t(SENTIMENT_LABEL_KEYS[cell.key]) })} &middot; {t(REPLACEMENT_BAND_LABEL_KEYS[cell.band])} ({bandRangeLabel(cell.band, b, t)})
              </h4>
              <span className="text-xs text-muted-foreground">
                {cellTotal.toLocaleString()} {cellTotal === 1 ? t('procAccountLower') : t('procAccountsLower')}
              </span>
            </div>
            {cellLoading ? (
              <div className="flex items-center py-6 text-xs text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('procLoadingAccounts')}
              </div>
            ) : (
            <div className="max-h-[420px] overflow-auto rounded border border-border">
              <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-muted">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('account')}</th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('vendor')}</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('procRcInstalled')}</th>
                    <th className="whitespace-nowrap px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('procRcAge')}</th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('product')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cellRows.map((i) => (
                    <tr key={i.install_key} className="border-t border-border align-top">
                      <td className="px-3 py-2">
                        <span className="font-semibold">{i.account_name}</span>
                        {i.country && <span className="block text-[11px] text-muted-foreground">{i.country}</span>}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{i.supplier}</td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums">{String(i.installed_on || '').slice(0, 10)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums">
                        {i.age_years == null ? '—' : `${i.age_is_floor ? '≥ ' : ''}${t('procRcYears', { n: Math.floor(i.age_years) })}`}
                      </td>
                      <td className="px-3 py-2 text-[12px] leading-relaxed text-muted-foreground">{i.product_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
